// The designs this browser has made. There are no accounts in the funnel, so "My designs" is
// simply the list of job ids this device has seen, kept in localStorage next to the basket.
// Everything shown on the page (image, status, revision count) is fetched fresh from the API by
// id — the stored entry is only the pointer plus enough to render before that lands.

export type StoredDesign = {
  jobId: string;
  // When this browser first saw the job. Only used to order the list before the API answers;
  // the server's timestamps win once loaded.
  savedAt: string;
  styleSlug?: string;
  thumbnailUrl?: string;
  // Tucked into the "Hidden" section of My designs. Kept rather than dropped so the design can be
  // shown again, and so a hidden id is still handed to the account at sign-in.
  hidden?: boolean;
};

const STORAGE_KEY = "vellum_designs";
// Plenty for a real customer, and it caps what the summary endpoint is asked for.
const MAX_DESIGNS = 50;

// Fired on any change so the nav badge / list can react within the same tab (the native
// "storage" event only fires in *other* tabs).
export const DESIGNS_EVENT = "vellum-designs-changed";

function emitChange() {
  if (typeof window !== "undefined") window.dispatchEvent(new Event(DESIGNS_EVENT));
}

export function subscribeDesigns(callback: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  const handler = () => callback();
  window.addEventListener(DESIGNS_EVENT, handler);
  window.addEventListener("storage", handler);
  return () => {
    window.removeEventListener(DESIGNS_EVENT, handler);
    window.removeEventListener("storage", handler);
  };
}

export function getDesigns(): StoredDesign[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (d): d is StoredDesign =>
        !!d && typeof d === "object" && typeof (d as StoredDesign).jobId === "string",
    );
  } catch {
    return [];
  }
}

function save(designs: StoredDesign[]) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(designs.slice(0, MAX_DESIGNS)));
  } catch {
    // ignore quota/serialization errors — the list is best-effort.
  }
  emitChange();
}

// Records a design, or patches the one already stored for this job. Newest first.
export function rememberDesign(jobId: string, patch: Omit<Partial<StoredDesign>, "jobId"> = {}): StoredDesign[] {
  if (!jobId) return getDesigns();
  const designs = getDesigns();
  const existing = designs.find((d) => d.jobId === jobId);
  const next: StoredDesign = {
    jobId,
    savedAt: existing?.savedAt ?? new Date().toISOString(),
    ...(existing ?? {}),
    ...patch,
  };
  const merged = [next, ...designs.filter((d) => d.jobId !== jobId)];
  save(merged);
  return merged;
}

export function setDesignHidden(jobId: string, hidden: boolean): StoredDesign[] {
  const designs = getDesigns().map((d) => (d.jobId === jobId ? { ...d, hidden } : d));
  save(designs);
  return designs;
}

/** The designs on show — what the nav badge counts. Hidden ones are not a reason to open the list. */
export function getDesignCount(): number {
  return getDesigns().filter((d) => !d.hidden).length;
}
